import React, { useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import IconEdit from '@assets/images/icons/pencil.svg'
import IconInfo from '@assets/images/icons/info-icon.svg'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import useCurrentModuleContext from '@/modules/_helpers/accessCheck/useCurrentModuleContext'
import SettingsRadioInput from '@/modules/base/components/settings-radio-input/settings-radio-input'
import { ChartOfAccountsSkrTypeService } from '@/modules/settings/services/chartOfAccountsSkrTypeService'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { useNavigate } from 'react-router'

const ChartOfAccounts = () => {
  const navigate = useNavigate()
  const classes = useStyles()
  const [skrTypeOptions, setSkrTypeOptions] = useState([])

  const { rights } = useCurrentModuleContext()
  const readOnly = !rights.write

  useEffect(() => {
    ChartOfAccountsSkrTypeService.getCurrentTenantSkrType()
      .then(async(skrType) => {
        const options = await ChartOfAccountsSkrTypeService.getSkrTypeOptions()
        return { skrType, options }
      })
      .then(({ skrType, options }) => {
        setSkrOptionsInState(options, skrType)
      })
  }, [])

  const onEditClicked = () => {
    navigate('/settings/tax-settings/chart-of-accounts')
  }

  const setSkrOptionsInState = (skrOptionList, currentSkrType) => {
    const tmpSkrTypeOptions = skrOptionList.map(e => ({ ...e, current: e.type === currentSkrType }))
    setSkrTypeOptions(tmpSkrTypeOptions)
  }

  const onSkrTypeChange = (value) => {
    const optionInfo = skrTypeOptions.find(e => e.type === value)
    if (optionInfo.current) {
      return
    }

    ChartOfAccountsSkrTypeService.updateTenantSkrType(value)
      .then(() => {
        setSkrOptionsInState(skrTypeOptions, value)
        const message = lang.settings.taxes.chartsOfAccountsSkrType.changeTypeSuccessfullMessage(optionInfo.caption)
        toast.showToast({ message })
      })
      .catch((error) => {
        toast.showToast({ error, message: lang.settings.taxes.chartsOfAccountsSkrType.changeTypeErrorMessage })
      })

  }

  const getSkrTypeOptionToolTip = (skrOption) => {
    const toolTip = lang.settings.taxes.chartsOfAccountsSkrType.options[skrOption]?.tooltip
    if (!toolTip) {
      return null
    }

    return (
      <div className={classes.tooltip}>
        <div className={classes.tooltipTitle}>{toolTip.title}</div>
        <div className={classes.tooltipContent}>{toolTip.text}</div>
      </div>
    )
  }

  const renderChartOfAccountsSkrOptions = () => {
    const mappedOptions = skrTypeOptions.map((option, index) => {
      const { type: value, caption: title, current: checked } = option
      const toolTipContent = getSkrTypeOptionToolTip(value)
      return (
        <div className={classes.chartOfAccountsSkrTypeOption}>
          <SettingsRadioInput key={index} title={title} value={value} checked={checked} onChange={onSkrTypeChange} readOnly={readOnly} />
          {toolTipContent
            ? <RichTooltip content={toolTipContent} width={560}>
              <SVGIcon svg={IconInfo} height="16px" width="16px" />
            </RichTooltip>
            : null}
        </div>
      )
    })

    return (
      <div className={classes.chartOfAccountsSkrTypeOptionContainer}>
        {mappedOptions}
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <div className={classes.title}>{lang.settings.taxes.chartOfAccounts}</div>
      <div className={classes.description}>{lang.settings.taxes.chartOfAccountsDescription}</div>
      <div className={classes.chartOfAccountsSkrTypeContainer}>
        <div>{lang.settings.taxes.chartsOfAccountsSkrType.title}</div>
        <div>{renderChartOfAccountsSkrOptions()}</div>
      </div>
      <div className={classes.buttonContainer}>
        <ButtonIconSquare
          label={lang.settings.taxes.editChartOfAccounts}
          icon={IconEdit}
          customClass={classes.button}
          onClick={onEditClicked}
          disabled={readOnly}
          dataQsId='settings-mycompany-bookkeeping-editChartOfAccounts-save'
        />
      </div>
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: theme.fontWeights.medium,
    fontSize: theme.fontSizes.smedium,
    marginBottom: '30px',
  },
  chartOfAccountsSkrTypeContainer: {
    marginTop: '35px',
    fontWeight: theme.fontWeights.medium,
  },
  chartOfAccountsSkrTypeOptionContainer: {
    marginTop: '20px',
  },
  chartOfAccountsSkrTypeOption: {
    display: 'flex',
    gap: '10px',
  },
  buttonContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    paddingTop: '30px',
  },
  button: {
    width: '250px',
  },
  tooltip: {
    height: 145,
    color: theme.black,
  },
  tooltipTitle: {
    fontSize: 14,
    lineHeight: '20px',
    color: theme.black,
    fontWeight: theme.fontWeights.bold,
  },
  tooltipContent: {
    fontSize: 12,
    marginTop: 20,
    '& strong': {
      fontWeight: theme.fontWeights.semibold,
    },
  },
}))

export default ChartOfAccounts
